// Libs
import classNames from 'classnames/bind';
import { pdf } from '@react-pdf/renderer';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import {
  NotesQuoteForm,
  QuoteItemsForm,
  GroupsQuoteForm,
  SummaryQuoteForm,
  TemplateQuoteForm,
  AttachmentsQuoteForm,
  EventDetailsQuoteForm,
  EventTimelineQuoteForm,
  ClientInformationQuoteForm,
  AdditionalChargesQuoteForm,
  QuotePdf,
} from '~/components/module/quotes';
import { BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseAddress, IBodyUpdate } from '~/utils/interface/common';
import { createQuote, getDetailQuote, updateQuote } from '~/thunks/quote/quoteThunk';
import { convertDataDetailQuotesForm, defaultValuesQuotesForm, schema } from './helper';
import {
  DiscountTypeEnum,
  QuoteStatusEnum,
  RoleEnum,
  TypeListInventory,
  TypeQuoteDetailEnum,
} from '~/utils/enum/common';
import { cleanObject, convertTimeToHHmm, createFormData, getFullName, removeEmptyObjects } from '~/utils/helper/common';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_INVOICE_FILE_NAME_PDF,
  DEFAULT_QUOTE_FILE_NAME_PDF,
  EMPTY_STRING,
  optionsDepositMade,
} from '~/utils/constants/common';
import {
  IClientQuoteDetail,
  IFormQuotes,
  IProductQuoteItem,
  IQueryParamQuoteDetail,
  IQuoteAddOn,
  IQuoteDetail,
  IQuoteItem,
  IQuotePdf,
} from '~/utils/interface/quotes';
import { StorageEnum } from '~/utils/enum/base';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './QuotesForm.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const QuotesForm = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: quoteId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    resetField,
    reset,
    trigger,
    setError,
    getValues,
    formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted, defaultValues },
  } = useForm<IFormQuotes>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesQuotesForm,
  });

  const { listQuoteItems, listQuoteItemsSelected, quoteItems, depositMade } = watch();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { profile } = useAppSelector((store) => store.user);
  //#endregion Selector

  //#region Declare State
  const [managedByNameDefault, setManagedByNameDefault] = useState<string>(EMPTY_STRING);
  const [salesPersonNameDefault, setSalesPersonNameDefault] = useState<string>(EMPTY_STRING);
  const [couponNameDefault, setCouponNameDefault] = useState<string>(EMPTY_STRING);

  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [removeCustomIds, setRemoveCustomIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [openSave, setOpenSave] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('managedBy', profile?.id);
    setValue('salesAccountId', profile?.id);
  }, [profile]);

  useEffect(() => {
    if (!quoteId) return;

    const params: IQueryParamQuoteDetail = {
      quoteId: quoteId,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetDetailQuote(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  useEffect(() => {
    if (!quoteId && listQuoteItemsSelected && listQuoteItemsSelected?.length > 0) return;

    const listQuoteItemsSelectedData = listQuoteItems?.filter((item) =>
      quoteItems?.find(
        (selected) =>
          selected.inventoryId == item.id && (selected.variantId || EMPTY_STRING) === (item.variantId || EMPTY_STRING)
      )
    );

    setValue('listQuoteItemsSelected', listQuoteItemsSelectedData);
  }, [listQuoteItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuote = async (params: IQueryParamQuoteDetail) => {
    setIsLoading(true);

    dispatch(getDetailQuote(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const dataDetail = res?.data as IQuoteDetail;

        const body: IFormQuotes = convertDataDetailQuotesForm(dataDetail);

        setManagedByNameDefault(
          getFullName({ firstName: dataDetail?.managedBy?.firstName, lastName: dataDetail?.managedBy?.lastName })
        );

        setSalesPersonNameDefault(
          getFullName({ firstName: dataDetail?.salesPerson?.firstName, lastName: dataDetail?.salesPerson?.lastName })
        );

        setCouponNameDefault(
          `${dataDetail?.quoteCoupon?.name} - ${handleConvertNameCoupon(
            String(dataDetail?.quoteCoupon?.discountRate),
            String(dataDetail?.quoteCoupon?.discountType)
          )}`
        );

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConvertNameCoupon = (discountRate: string, discountType: string) => {
    switch (discountType) {
      case DiscountTypeEnum.PERCENTAGE:
        return `${discountRate}%`;
      case DiscountTypeEnum.AMOUNT:
        return `$${discountRate}`;
      default:
        return EMPTY_STRING;
    }
  };

  const handleFormQuote = (data: IFormQuotes, status: QuoteStatusEnum, isConvert?: boolean) => {
    if (quoteId) {
      handleEditQuote(data, status, isConvert);
    } else {
      handleCreateQuote(data, status, isConvert);
    }
  };

  const generatePdfBlob = async (data: IFormQuotes): Promise<Blob | null> => {
    try {
      const client = {
        firstName: data?.firstName || EMPTY_STRING,
        lastName: data?.lastName || EMPTY_STRING,
        clientContacts: data?.clientContacts || [],
      } as IClientQuoteDetail;

      const quotePdf: IQuotePdf = {
        client: client,
        poNumber: data?.poNumber || EMPTY_STRING,
        startDate: data?.startDate || EMPTY_STRING,
        startTime: data?.startTime || EMPTY_STRING,
        endDate: data?.endDate || EMPTY_STRING,
        endTime: data?.endTime || EMPTY_STRING,
        quoteItems: data?.quoteItems || [],
        quoteAddOns: data?.quoteAddOns || [],
        deliveryCost: data?.deliveryCost || EMPTY_STRING,
        damageWaiver: data?.damageWaiver || EMPTY_STRING,
        laborFee: data?.laborFee || EMPTY_STRING,
        discountRate: data?.discountRate || EMPTY_STRING,
        depositMadeAmount: data?.depositMadeAmount || EMPTY_STRING,
        couponAmount: data?.couponAmount || EMPTY_STRING,
        customFees: data?.customFees || [],
        productSubtotal: data?.pricingTotal || EMPTY_STRING,
        total: data?.total || EMPTY_STRING,
        taxAmount: data?.taxAmount || EMPTY_STRING,
        mileage: data?.mileage || EMPTY_STRING,
        externalNote: data?.externalNote || EMPTY_STRING,
        internalNote: data?.internalNote || EMPTY_STRING,
        dataProduct: handleDataTableOrderProduct(data?.quoteItems || [], data?.quoteAddOns || []) || [],
      };

      return await pdf(<QuotePdf quotePdf={quotePdf} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };

  const handleDataTableOrderProduct = (orderItems: IQuoteItem[], orderItemAddOns: IQuoteAddOn[]) => {
    const orderItemMap = orderItems.reduce<Record<string, IQuoteItem>>((acc, item) => {
      acc[item?.id || EMPTY_STRING] = item;
      return acc;
    }, {});

    const addOnMap = orderItemAddOns.reduce<Record<string, IProductQuoteItem[]>>((acc, addOn) => {
      const parent = orderItemMap[addOn?.parentId || EMPTY_STRING];
      const parentType = parent ? parent.type : EMPTY_STRING;

      if (!acc[addOn?.parentId || EMPTY_STRING]) acc[addOn?.parentId || EMPTY_STRING] = [];
      acc[addOn?.parentId || EMPTY_STRING].push({
        ...addOn,
        parentType,
      } as IProductQuoteItem);

      return acc;
    }, {});

    const result: IProductQuoteItem[] = [];

    for (const item of orderItems) {
      result.push(item as IProductQuoteItem);
      const addOns = addOnMap[item?.id || EMPTY_STRING];
      if (addOns && addOns.length > 0) {
        result.push(...addOns);
      }
    }

    return result;
  };

  const updateOverbooking = (data: IFormQuotes) => {
    const { quoteItems = [], quoteAddOns = [] } = data;

    const handleSumQuantity = (inventoryId: string, variantId?: string) => {
      return [...(quoteItems ?? []), ...(quoteAddOns ?? [])]
        .filter(
          (item) =>
            item?.inventoryId === inventoryId && (item?.variantId || EMPTY_STRING) === (variantId || EMPTY_STRING)
        )
        .reduce((sum, item) => sum + Number(item?.quantity), 0);
    };

    const updateItem = <
      T extends {
        inventoryId?: string;
        variantId?: string;
        quantity: number;
        availableInventory?: string;
        name?: string;
        type?: string;
      }
    >(
      items: T[]
    ): T[] => {
      return items?.map((item) => {
        if (!item?.inventoryId) return { ...item, isOverbooking: false };

        const used = handleSumQuantity(item?.inventoryId, item?.variantId);

        const isOver = item?.type !== TypeListInventory.PACKAGE ? used > Number(item?.availableInventory || 0) : false;

        return { ...item, isOverbooking: isOver };
      });
    };

    const updatedQuoteItems = updateItem(quoteItems);
    const updatedQuoteAddOns = updateItem(quoteAddOns);
    return {
      ...data,
      quoteItems: updatedQuoteItems,
      quoteAddOns: updatedQuoteAddOns,
      isOverbooking: [...updatedQuoteItems, ...updatedQuoteAddOns].some((i) => i?.isOverbooking),
    };
  };

  const handleCreateQuote = async (data: IFormQuotes, status: QuoteStatusEnum, isConvert?: boolean) => {
    setIsLoading(true);

    const checkedData = updateOverbooking(data);

    const {
      listQuoteItems,
      selectGroupQuote,
      templateQuoteId,
      couponType,
      couponRate,
      selectGroupItem,
      selectIdProductQuote,
      selectIdProductGroup,
      selectIdVariantQuote,
      selectIdVariantGroup,
      selectQuantityProduct,
      deleteProduct,
      quoteItemQuantity,
      customFees,
      deleteChildrenProduct,
      clientAddress,
      amountDue,
      quoteFile,
      listQuoteItemsSelected,
      deleteItemIds,
      ...restData
    } = checkedData;

    const convertData: IFormQuotes = {
      ...restData,
      status,
      isConvert,
      earliestDeliveryTime: convertTimeToHHmm(restData?.earliestDeliveryTime) || undefined,
      latestPickupTime: convertTimeToHHmm(restData?.latestPickupTime) || undefined,
      endTime: convertTimeToHHmm(restData?.endTime) || undefined,
      startTime: convertTimeToHHmm(restData?.startTime) || undefined,
      pickupScheduledTime: convertTimeToHHmm(restData?.pickupScheduledTime) || undefined,
      deliveryScheduledTime: convertTimeToHHmm(restData?.deliveryScheduledTime) || undefined,
      quoteGroups: checkedData?.quoteGroups?.map(({ id, idDetail, ...rest }) => rest),
      quoteItems: checkedData?.quoteItems?.map(
        ({ id, idDetail, category, categoryId, categoryName, availableInventory, type, ...rest }) => rest
      ),
      quoteAddOns: checkedData?.quoteAddOns
        ?.filter((item) => item?.isShow)
        .map(({ id, idDetail, idRemember, isHasVariant, availableInventory, type, isShow, ...rest }) => rest),
      customFees: customFees?.map(({ id, ...rest }) => rest),
      ...(clientAddress &&
        ({
          clientAddress: {
            address: clientAddress.address ?? EMPTY_STRING,
            unitDetail: clientAddress.unitDetail ?? EMPTY_STRING,
            city: clientAddress.city ?? EMPTY_STRING,
            state: clientAddress.state ?? EMPTY_STRING,
            postalCode: clientAddress.postalCode ?? EMPTY_STRING,
            // country: clientAddress.country ?? EMPTY_STRING,
            siteType: clientAddress.siteType ?? EMPTY_STRING,
            lat: clientAddress.lat ?? EMPTY_STRING,
            lng: clientAddress.lng ?? EMPTY_STRING,
          },
        } as IBaseAddress)),
    };

    const formData = await createFormData(removeEmptyObjects(convertData), ['quoteFile']);

    const pdfBlob = await generatePdfBlob(data);
    if (pdfBlob) {
      formData.append('boldSignFile', pdfBlob, DEFAULT_QUOTE_FILE_NAME_PDF);
      formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);
    }

    dispatch(createQuote(formData))
      .unwrap()
      .then((_res) => {
        status === QuoteStatusEnum.PENDING_APPROVAL ? handleCancelOrder() : handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditQuote = async (data: IFormQuotes, status: QuoteStatusEnum, isConvert?: boolean) => {
    setIsLoading(true);

    const checkedData = updateOverbooking(data);

    const {
      listQuoteItems,
      selectGroupQuote,
      selectGroupItem,
      selectIdProductQuote,
      selectIdProductGroup,
      selectIdVariantQuote,
      selectIdVariantGroup,
      selectQuantityProduct,
      listQuoteItemsSelected,
      deleteProduct,
      deleteChildrenProduct,
      // businessType,
      quoteItemQuantity,
      customFees,
      couponType,
      templateQuoteId,
      couponRate,
      quoteFile,
      amountDue,
      clientAddress,
      isTemplate,
      ...restData
    } = checkedData;

    const convertData: IFormQuotes = {
      ...restData,
      status,
      isConvert,
      earliestDeliveryTime: convertTimeToHHmm(restData?.earliestDeliveryTime) || undefined,
      latestPickupTime: convertTimeToHHmm(restData?.latestPickupTime) || undefined,
      endTime: convertTimeToHHmm(restData?.endTime) || undefined,
      startTime: convertTimeToHHmm(restData?.startTime) || undefined,
      pickupScheduledTime: convertTimeToHHmm(restData?.pickupScheduledTime) || undefined,
      deliveryScheduledTime: convertTimeToHHmm(restData?.deliveryScheduledTime) || undefined,
      paymentTerms: restData?.paymentTerms || undefined,
      deliveryAddress: cleanObject(restData?.deliveryAddress, { removeEmptyStrings: false, removeNull: true }),
      pickupAddress: cleanObject(restData?.pickupAddress, { removeEmptyStrings: false, removeNull: true }),
      couponId: restData?.couponId || undefined,
      deliveryCost: restData?.deliveryCost || '0',
      damageWaiver: restData?.damageWaiver || '0',
      laborFee: restData?.laborFee || '0',
      discountRate: restData?.discountRate || '0',
      mileage: restData?.mileage || '0',
      taxRate: restData?.taxRate || '0',
      quoteGroups: restData?.quoteGroups?.map(({ id, idDetail, ...rest }) => {
        const convertData = {
          ...rest,
          discountRate: rest.discountRate || '0',
          position: rest.position || undefined,
        };

        const shouldAddId = idDetail && !isNaN(Number(id));

        return {
          ...convertData,
          ...(shouldAddId ? { id: idDetail } : {}),
        };
      }),
      quoteItems: restData?.quoteItems?.map(
        ({
          id,
          category,
          categoryId,
          categoryName,
          idDetail,
          type,
          productId,
          isDelete,
          parentId,
          availableInventory,
          ...rest
        }) => {
          const newItem = cleanObject(rest, { removeEmptyStrings: false, removeNull: true }) as IQuoteItem;

          const convertData = {
            ...newItem,
            laborCost: rest.laborCost || '0',
            discountRate: rest.discountRate || '0',
            variantId: rest.variantId || undefined,
            variantName: rest.variantName || undefined,
            position: rest.position || undefined,
          };
          return idDetail ? { id: idDetail, ...convertData } : convertData;
        }
      ),
      quoteAddOns: restData?.quoteAddOns
        ?.filter((item) => item?.isShow)
        ?.map(
          ({
            id,
            idDetail,
            type,
            parentId,
            productId,
            availableInventory,
            idRemember,
            isHasVariant,
            isShow,
            ...rest
          }) => {
            const newItem = cleanObject(rest, { removeEmptyStrings: false, removeNull: true }) as IQuoteAddOn;

            const convertData = {
              ...newItem,
              laborCost: rest.laborCost || '0',
              discountRate: rest.discountRate || '0',
              variantId: rest.variantId || undefined,
              variantName: rest.variantName || undefined,
              position: rest.position || undefined,
              discountType: rest?.discountType || DiscountTypeEnum.PERCENTAGE,
            };
            return idDetail ? { id: idDetail, ...convertData } : convertData;
          }
        ),

      ...(customFees && {
        customFees: customFees.map(({ id, ...rest }) => {
          return id && id.length > 10 ? rest : { id, ...rest };
        }),
      }),

      ...(clientAddress &&
        ({
          clientAddress: {
            address: clientAddress.address ?? EMPTY_STRING,
            unitDetail: clientAddress.unitDetail ?? EMPTY_STRING,
            city: clientAddress.city ?? EMPTY_STRING,
            state: clientAddress.state ?? EMPTY_STRING,
            postalCode: clientAddress.postalCode ?? EMPTY_STRING,
            // country: clientAddress.country ?? EMPTY_STRING,
            siteType: clientAddress.siteType ?? EMPTY_STRING,
            lat: clientAddress.lat ?? EMPTY_STRING,
            lng: clientAddress.lng ?? EMPTY_STRING,
          },
        } as IBaseAddress)),

      ...(quoteFile && {
        quoteFile: quoteFile.filter((item) => !item.id),
      }),
      ...(removeFileIds.length && { deleteFileIds: removeFileIds }),
      ...(removeCustomIds.length && { deleteCustomIds: removeCustomIds }),
    };

    const formData = await createFormData(convertData, ['quoteFile']);

    // const pdfBlob = await generatePdfBlob(data);
    // if (pdfBlob) {
    //   formData.append('boldSignFile', pdfBlob, DEFAULT_QUOTE_FILE_NAME_PDF);
    //   formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);
    // }

    const params: IBodyUpdate<FormData> = {
      id: quoteId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateQuote(params))
      .unwrap()
      .then((_res) => {
        status === QuoteStatusEnum.PENDING_APPROVAL ? handleCancelOrder() : handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleRemoveFile = (fileId: string) => {
    setRemoveFileIds((prev) => [...prev, fileId]);
  };

  const handleRemoveCustom = (customId: string) => {
    if (customId.length > 15) return;

    setRemoveCustomIds((prev) => [...prev, customId]);
  };

  const handleToggle = () => {
    setOpenSave((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpenSave(false);
  };

  const handleCancel = () => {
    if (quoteId) {
      navigate(BACK_TO_PREVIOUS_PAGE);
    } else {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.quotes);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.quotes);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.quotes);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.quotes);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.quotes);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.quotes);
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.quotes);
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.quotes);
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.quotes);
          break;
      }
    }
    reset();
  };

  const handleCancelOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.order);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.order);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.order);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.order);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.order);
        break;
    }
    reset();
  };
  //#endregion Handle Function

  return (
    <Box id='quotesPage' className={cx('container')}>
      <BaseToolbar title={t('form_quotes_title_build_quote')} isBack></BaseToolbar>

      <FormProvider
        {...({
          control,
          setError,
          resetField,
          watch,
          reset,
          trigger,
          setValue,
          getValues,
          formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted, defaultValues },
        } as UseFormReturn<IFormQuotes>)}
      >
        <TemplateQuoteForm />

        <ClientInformationQuoteForm />

        <EventDetailsQuoteForm
          managedByNameDefault={managedByNameDefault}
          salesPersonNameDefault={salesPersonNameDefault}
        />

        <EventTimelineQuoteForm />

        <QuoteItemsForm />

        {listQuoteItems && <GroupsQuoteForm />}

        <NotesQuoteForm />

        <AttachmentsQuoteForm onRemoveFile={handleRemoveFile} />

        <AdditionalChargesQuoteForm couponNameDefault={couponNameDefault} onRemoveCustom={handleRemoveCustom} />

        <SummaryQuoteForm />
      </FormProvider>

      <Stack
        direction={'row'}
        gap={3}
        alignItems={'center'}
        justifyContent={'flex-end'}
        sx={{
          // position: 'sticky',
          bottom: 0,
          bgColor: 'background.paper',
          p: 2,
          borderTop: '1px solid #ddd',
        }}
      >
        <Button variant='outlined' className={cx('buttonSaveDraft')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <ButtonGroup
          variant='contained'
          className={cx('buttonGroup')}
          ref={anchorRef}
          aria-controls={openSave ? 'split-button-menu' : undefined}
          aria-expanded={openSave ? 'true' : undefined}
          aria-haspopup='menu'
          onClick={handleToggle}
        >
          <Button variant='contained' disableTouchRipple className={cx('buttonSendQuote')}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
          <Button disableTouchRipple className={cx('buttonDown')} size='small'>
            <img
              src={icons.commonIconArrowBottomWhite}
              alt={t('common_alt_img', { ns: 'common/base' })}
              width={20}
              height={20}
            />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{ zIndex: 1, pb: 2 }}
          open={openSave}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id='split-button-menu' autoFocusItem className={cx('menu')}>
                    {[
                      t(quoteId ? 'form_quotes_button_edit_quote' : 'form_quotes_button_create_quote'),
                      t('form_quotes_button_create_order'),
                    ].map((option, index) => (
                      <MenuItem
                        className={cx('menuItem')}
                        key={option}
                        disabled={index === 1 && depositMade !== optionsDepositMade[0].value}
                        onClick={() => {
                          setOpenSave(false);
                          handleSubmit((data) =>
                            handleFormQuote(
                              data,
                              index === 1 ? QuoteStatusEnum.PENDING_APPROVAL : QuoteStatusEnum.IN_PROCESS,
                              index === 1
                            )
                          )();
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Stack>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default QuotesForm;
